import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Injectable,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import {
  DateFilterFn,
  MatDatepicker,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';

import {
  DateAdapter,
  MatNativeDateModule,
  MatOptionModule,
  NativeDateAdapter,
} from '@angular/material/core';

import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { OverlayContainer } from '@angular/cdk/overlay';
import { BreakpointService, WindowRef } from '@boels/helpers';
import {
  BehaviorSubject,
  concatMap,
  filter,
  map,
  Observable,
  of,
  Subscription,
  throttleTime,
} from 'rxjs';
import { CartFacade, HolidaysFacade } from '@boels/shared-state';
import { BoelsReservation, CartDate, Month } from '@boels/models';

@Injectable()
export class BoelsDateAdapter extends NativeDateAdapter {
  override getFirstDayOfWeek(): number {
    return 1;
  }

  override format(date: Date): string {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return day + '-' + month + '-' + year;
  }
}

type DateObjectConstruct = Array<{ year: number; month: number; day: number }>;

@Component({
  selector: 'boels-date-picker',
  imports: [
    CommonModule,
    TranslocoModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatOptionModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    FontAwesomeModule,
  ],
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    HolidaysFacade,
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['addToCart'],
    },
    { provide: DateAdapter, useClass: BoelsDateAdapter },
  ],
})
export class DatePickerComponent implements OnInit, OnDestroy {
  private breakpointService = inject(BreakpointService);
  private overlay = inject(OverlayContainer);
  private renderer = inject(Renderer2);
  private ref = inject(ElementRef);
  private cartFacade = inject(CartFacade);
  private winRef = inject(WindowRef);
  private holidaysFacade = inject(HolidaysFacade);

  @ViewChild('startDatePicker') startDatePicker: MatDatepicker<Date>;
  @ViewChild('endDatePicker') endDatePicker?: MatDatepicker<Date>;
  @Output() datesSelected: EventEmitter<CartDate> =
    new EventEmitter<CartDate>();
  protected subs$: Subscription = new Subscription();
  @Input() startDate: string | undefined;
  @Input() endDate: string | undefined;
  @Input() minDate: Date = new Date();
  @Input() maxStartDate: Date | null = null;
  @Input() minEndDate: Date = new Date();
  @Input() maxDate: Date | null = null;
  @Input() cutOffTimestamp: string | undefined;
  @Input() isSingleDateMode = false;
  @Input() startDateLabel = '';
  @Input() endDateLabel = '';
  @Output() datesPassed: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() isCart = false;

  isMobile$: Observable<boolean> = this.breakpointService.smAndBelow$;
  faCalenderAlt = faCalendarAlt;
  cartForm: FormGroup;
  private nonWorkingDates$: BehaviorSubject<
    Array<{ year: number; month: number; day: number }>
  > = new BehaviorSubject<DateObjectConstruct>([]);

  ngOnInit(): void {
    this.initDatePickerForm();
    this.updateDatePickerForm();
    this.getNonWorkingDays();
  }

  initDatePickerForm() {
    this.cartForm = new FormGroup({
      startDate: new FormControl('', Validators.required),
      endDate: new FormControl(''),
    });
    if (!this.isSingleDateMode) {
      this.cartForm?.get('endDate')?.setValidators(Validators.required);
    }

    this.onChangesDatePickerForm();
  }

  updateDatePickerForm(): void {
    const today = new Date().setHours(0, 0, 0, 0);
    const cutOffTime =
      this.cutOffTimestamp && new Date(this.cutOffTimestamp).getTime();
    const startDateAsNumber =
      this.startDate && new Date(this.startDate).setHours(0, 0, 0, 0);

    if (
      startDateAsNumber &&
      ((cutOffTime &&
        startDateAsNumber === today &&
        this.isFirstDateBeforeSecondDate(cutOffTime, new Date().getTime())) ||
        this.isFirstDateBeforeSecondDate(startDateAsNumber, today))
    ) {
      this.startDate = undefined;
    }

    if (this.startDate && this.endDate) {
      this.cartForm.patchValue({
        startDate: this.startDate,
        endDate: this.endDate,
      });

      if (
        this.isFirstDateBeforeSecondDate(
          new Date(this.startDate).getTime(),
          today,
        ) ||
        (this.endDate &&
          this.isFirstDateBeforeSecondDate(
            new Date(this.endDate).getTime(),
            today,
          ))
      )
        this.datesPassed.emit(true);
    }
  }

  private isFirstDateBeforeSecondDate(
    firstDate: number,
    secondDate: number,
  ): boolean {
    return firstDate < secondDate;
  }

  onChangesDatePickerForm() {
    this.subs$ = this.cartForm.valueChanges
      .pipe(throttleTime(500))
      .subscribe((value: CartDate) => {
        this.setMinAndMaxDate(value);

        if (
          this.cartForm.valid ||
          (this.isSingleDateMode && this.cartForm.get('startDate')?.valid)
        ) {
          this.datesSelected.emit({
            startDate: value.startDate,
            ...(this.endDate && { endDate: value.endDate }),
          });
          this.datesPassed.emit(false);
          if (value.endDate) {
            this.updateCart(
              value.startDate.toString(),
              value.endDate.toString(),
            );
          }
        }
      });
  }

  updateCart(startDate: string, endDate: string) {
    this.cartFacade.updateCartDates(
      this._formatDateToLocal(startDate),
      this._formatDateToLocal(endDate),
    );
  }

  protected _formatDateToLocal(dateString: string): string {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  setMinAndMaxDate(date: CartDate) {
    this.minEndDate = date.startDate ? date.startDate : new Date();
    if (
      (!date.startDate && date.endDate) ||
      (date.endDate &&
        new Date(date.endDate)?.getTime() > new Date(date.startDate)?.getTime())
    ) {
      this.maxStartDate = date.endDate;
    }
    if (date.endDate) this.maxStartDate = date.endDate;
  }

  getNonWorkingDays(): void {
    const today = new Date();
    const nextTwoYears = new Date(
      today.getFullYear() + 2,
      today.getMonth(),
      today.getDate(),
    );

    if (this.isCart) {
      this.subs$ = this.cartFacade
        .getReservationCalendar(
          this._formatReservationDate(today.toISOString().substring(0, 10)),
          this._formatReservationDate(
            nextTwoYears.toISOString().substring(0, 10),
          ),
        )
        .pipe(
          concatMap((reservation: BoelsReservation) =>
            this.setNonworkingDatesForNextTwoYears(reservation),
          ),
        )
        .subscribe();
    } else {
      this.subs$ = this.holidaysFacade
        .getHolidays(
          this.holidaysFacade.convertToIsoCode8601(today),
          this.holidaysFacade.convertToIsoCode8601(nextTwoYears),
        )
        .pipe(
          filter(Boolean),
          concatMap((holidays: BoelsReservation) =>
            this.setNonworkingDatesForNextTwoYears(holidays),
          ),
        )
        .subscribe();
    }
  }

  protected _formatReservationDate(dateString?: string): string {
    const datePart: string[] | null | undefined = dateString?.match(/\d+/g);
    const year = datePart && datePart[0].substring(2);
    const month = datePart && datePart[1];
    const day = datePart && datePart[2];

    return month + '/' + day + '/' + year;
  }

  setNonworkingDatesForNextTwoYears(
    reservations: BoelsReservation,
  ): Observable<DateObjectConstruct> {
    const nonWorkingDates: DateObjectConstruct = [];
    const currentTime = new Date().getTime();
    const cuttOffTime =
      this.cutOffTimestamp && new Date(this.cutOffTimestamp).getTime();
    const now = new Date();
    const today = {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
    };

    reservations?.years.forEach((year) => {
      year.months.forEach((month: Month) => {
        month.nonWorkingDays.forEach((days: number) =>
          nonWorkingDates.push({
            year: year.year,
            month: month.month,
            day: days,
          }),
        );
      });
    });

    if (cuttOffTime && cuttOffTime <= currentTime) {
      nonWorkingDates.push(today);
    }

    this.nonWorkingDates$.next([...nonWorkingDates]);
    return this.nonWorkingDates$;
  }

  nonWorkingDatesFilterWrapper: DateFilterFn<Date | null> = (
    date: Date | null,
  ) => {
    let isAllowed = false;
    this.nonWorkingDatesFilter(date).subscribe((result: boolean) => {
      isAllowed = result;
    });
    return isAllowed;
  };

  nonWorkingDatesFilter(date: Date | null): Observable<boolean> {
    if (!date) return of(true);

    const today = new Date().setHours(0, 0, 0, 0);

    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    if (date.getTime() < today) {
      return of(false);
    }

    return this.nonWorkingDates$.pipe(
      map((dates: DateObjectConstruct) => {
        for (const date of dates) {
          if (date.year === year && date.month === month && date.day === day) {
            return false;
          }
        }
        return true;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs$.unsubscribe();
    this.nonWorkingDates$.complete();
  }
}
